var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
import { bind as bind_, chainFirst as chainFirst_ } from 'fp-ts/Chain';
import { chainFirstIOK as chainFirstIOK_, chainIOK as chainIOK_ } from 'fp-ts/FromIO';
import { chainFirstTaskK as chainFirstTaskK_, chainTaskK as chainTaskK_ } from 'fp-ts/FromTask';
import { flow, identity, pipe, SK } from 'fp-ts/function';
import * as RA from 'fp-ts/ReadonlyArray';
import { EMPTY } from 'rxjs';
import { switchAll, switchMap } from 'rxjs/operators';
import { Applicative, Apply, FromIO, fromIO, fromObservable, fromOption, fromReadonlyArray, FromTask, fromTask, fromTaskOptionK, toReadonlyArray } from './Observable';
// -------------------------------------------------------------------------------------
// non-pipeables
// -------------------------------------------------------------------------------------
var _chain = function (ma, f) { return pipe(ma, chain(f)); };
// -------------------------------------------------------------------------------------
// combinators
// -------------------------------------------------------------------------------------
export var chainOptionK = 
/*#__PURE__*/
function (f) { return chain(flow(f, fromOption)); };
// -------------------------------------------------------------------------------------
// type class members
// -------------------------------------------------------------------------------------
/**
 * Composes computations in sequence, using the return value of one computation to determine the next computation.
 *
 * @category Monad
 */
export var chain = function (f) { return function (ma) { return ma.pipe(switchMap(f)); }; };
/**
 * Derivable from `Monad`.
 */
export var flatten = 
/*#__PURE__*/
switchAll();
// -------------------------------------------------------------------------------------
// instances
// -------------------------------------------------------------------------------------
/**
 * @category instances
 */
export var getMonoid = function () { return ({
    concat: function (x, y) { return flatten(fromReadonlyArray([x, y])); },
    empty: EMPTY
}); };
/**
 * @category instances
 */
export var Chain = __assign(__assign({}, Apply), { chain: _chain });
/**
 * Composes computations in sequence, using the return value of one computation to determine the next computation and
 * keeping only the result of the first.
 *
 * Derivable from `Monad`.
 */
export var chainFirst = chainFirst_(Chain);
/**
 * @category combinators
 */
export var chainIOK = 
/*#__PURE__*/
chainIOK_(FromIO, Chain);
/**
 * @category combinators
 */
export var chainFirstIOK = 
/*#__PURE__*/
chainFirstIOK_(FromIO, Chain);
/**
 * @category combinators
 */
export var chainTaskK = 
/*#__PURE__*/
chainTaskK_(FromTask, Chain);
/**
 * @category combinators
 */
export var chainFirstTaskK = 
/*#__PURE__*/
chainFirstTaskK_(FromTask, Chain);
/**
 * @category combinators
 */
export var chainTaskOptionK = flow(fromTaskOptionK, chain);
/**
 * @category instances
 */
export var Monad = __assign(__assign({}, Applicative), { chain: _chain });
/**
 * @category instances
 */
export var MonadIO = __assign(__assign({}, Applicative), { chain: _chain, fromIO: fromIO });
/**
 * @category instances
 */
export var MonadTask = __assign(__assign({}, Applicative), { chain: _chain, fromIO: fromIO, fromTask: fromTask });
export var MonadObservable = __assign(__assign({}, Applicative), { chain: _chain, fromIO: fromIO, fromTask: fromTask, fromObservable: fromObservable });
// -------------------------------------------------------------------------------------
// array utils
// -------------------------------------------------------------------------------------
/**
 * Equivalent to `ReadonlyArray#traverseWithIndex(ApplicativePar)`.
 */
export var traverseReadonlyArrayWithIndex = function (f) {
    return flow(RA.mapWithIndex(f), fromReadonlyArray, flatten, toReadonlyArray);
};
export var traverseReadonlyArray = function (f) { return traverseReadonlyArrayWithIndex(flow(SK, f)); };
export var sequenceReadonlyArray = 
/*#__PURE__*/
traverseReadonlyArray(identity);
// -------------------------------------------------------------------------------------
// do notation
// -------------------------------------------------------------------------------------
export var bind = bind_(Chain);
// -------------------------------------------------------------------------------------
// utils
// -------------------------------------------------------------------------------------
export { switchScan } from 'rxjs';
